// "I'll take this" on the page (milestone 10 check 7; D217; module 10 U25's five states). The API
// carries a shared item's facts in its features (`shared`, `claim`, written by the pass and brought
// up to date on every read, apps/api/src/claim/overlay.ts); this reads them into the state the card
// draws. Pure, so the words are proven without a DOM (claim.test.ts). The types live here rather than
// in api.ts, which is another track's file this milestone: the decide call itself is api.ts's
// existing triage.decide, since a take is an accepted option like any other.
import type { PassItem } from './api.js';

export interface SharedPerson { name: string; principal_id: string | null; away: boolean; addressee: boolean }
export interface SharedFeature {
  with: SharedPerson[];
  since: string;
  expected_reply_by: string | null;
  replied: { by: string; at: string } | null;
  account_owner: { principal_id: string | null; name: string } | null;
  asked_by: { principal_id: string; name: string; at: string; request_id: string } | null;
  asked: Array<{ principal_id: string; name: string; at: string }>;
}
export interface ClaimFeature { id: string; by_principal_id: string; by: string; at: string; mine: boolean; task_id: string | null }
export type ClaimState = 'untaken' | 'mine' | 'theirs' | 'warning' | 'asked';

/** What the decide route adds to its answer for a take, a give-back or a request (routes/triage-decide.ts). */
export interface ClaimDecideExtra {
  claim: { id: string } | null;
  asked: { requestId: string; to: string; name: string; landed: boolean; refused: string | null } | null;
}

/** The option kinds of this feature (a list in code on the API; the portal's OptionKind predates them). */
export const CLAIM_KINDS = new Set(['take', 'give_back', 'ask_to_take']);

const obj = (v: unknown): Record<string, unknown> | null => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null);

export function sharedOf(item: Pick<PassItem, 'features'>): SharedFeature | null {
  const s = obj((item.features as Record<string, unknown> | undefined)?.shared);
  return s && Array.isArray(s.with) ? (s as unknown as SharedFeature) : null;
}
export function claimOf(item: Pick<PassItem, 'features'>): ClaimFeature | null {
  const c = obj((item.features as Record<string, unknown> | undefined)?.claim);
  return c && typeof c.by_principal_id === 'string' ? (c as unknown as ClaimFeature) : null;
}

/** U25's state, exactly as the API decides it (claim/state.ts claimState): a claim first, then a request, then the window. */
export function claimState(item: Pick<PassItem, 'features'>, now: number = Date.now()): ClaimState | null {
  const claim = claimOf(item);
  if (claim) return claim.mine ? 'mine' : 'theirs';
  const shared = sharedOf(item);
  if (!shared) return null;
  if (shared.asked_by) return 'asked';
  if (!shared.replied && shared.expected_reply_by && Date.parse(shared.expected_reply_by) <= now) return 'warning';
  return 'untaken';
}

export function firstName(name: string | null | undefined): string {
  const f = (name ?? '').trim().split(/\s+/)[0];
  return f && f.length > 0 ? f : 'Someone';
}

/** "10:42", or "Tue 10:42" when it is not today, in the reader's own zone. */
export function clock(iso: string, now: number = Date.now()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  const sameDay = new Date(now).toDateString() === d.toDateString();
  return sameDay ? time : `${d.toLocaleDateString('en-GB', { weekday: 'short' })} ${time}`;
}

/** How long something has sat: "3 hours", "2 days". */
export function since(iso: string, now: number = Date.now()): string {
  const h = Math.max(0, Math.round((now - Date.parse(iso)) / 3_600_000));
  if (h < 1) return 'less than an hour';
  if (h < 48) return `${h} hour${h === 1 ? '' : 's'}`;
  const d = Math.round(h / 24);
  return `${d} days`;
}

/** "Mike, Conor and Will (away)". */
export function names(people: Array<{ name: string; away?: boolean }>): string {
  const list = people.map((p) => `${firstName(p.name)}${p.away ? ' (away)' : ''}`);
  if (list.length <= 1) return list.join('');
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
}

export interface ClaimWords {
  state: ClaimState;
  /** The pills beside the item's own (U25). */
  pills: Array<{ text: string; tone: 'bad' | 'you' | 'away' | 'plain' }>;
  /** The line under the gist: who else it is with, or who replied, or who asked. */
  line: string | null;
  /** The warning card's own headline (state 4). */
  headline: string | null;
  /** The warning card's gist (state 4). */
  gist: string | null;
}

/** The words a card carries in each state (U25), from the item's features. Null for an item that is not shared. */
export function claimWords(item: Pick<PassItem, 'features' | 'headline'>, now: number = Date.now()): ClaimWords | null {
  const state = claimState(item, now);
  if (!state) return null;
  const shared = sharedOf(item);
  const claim = claimOf(item);
  const others = shared?.with ?? [];
  const pills: ClaimWords['pills'] = [];
  let line: string | null = null;
  let headline: string | null = null;
  let gist: string | null = null;
  switch (state) {
    case 'untaken':
      if (shared?.replied) pills.push({ text: `${firstName(shared.replied.by)} replied at ${clock(shared.replied.at, now)}`, tone: 'plain' });
      else pills.push({ text: 'Nobody has taken this', tone: 'bad' });
      break;
    case 'warning':
      pills.unshift({ text: `Untaken for ${since(shared!.since, now)}`, tone: 'bad' });
      headline = `Nobody has taken this: ${item.headline}`;
      gist = `It has been with you${others.length > 0 ? `, ${names(others)}` : ''} since ${clock(shared!.since, now)}.${shared!.account_owner ? ` ${shared!.account_owner.name} owns the account.` : ''}`;
      break;
    case 'mine':
      pills.push({ text: `Yours · taken ${clock(claim!.at, now)}`, tone: 'you' });
      if (others.length > 0) line = `${names(others.map((p) => ({ name: p.name })))} can see you have it.`;
      break;
    case 'theirs':
      pills.push({ text: `${firstName(claim!.by)} has it`, tone: 'plain' });
      break;
    case 'asked':
      pills.push({ text: `${firstName(shared!.asked_by!.name)} asked you to take this`, tone: 'you' });
      break;
  }
  return { state, pills, line, headline, gist };
}

/** "Also with" (U25 state 1): the other internal people on the mail, principals or not, the away ones dashed. */
export function alsoWith(item: Pick<PassItem, 'features'>): SharedPerson[] {
  const s = claimState(item);
  if (s !== 'untaken' && s !== 'warning' && s !== 'asked') return [];
  return sharedOf(item)?.with ?? [];
}

/** The awareness line (U25 state 3): "Conor has <the item>", taken when. */
export function theirLine(item: Pick<PassItem, 'features' | 'headline'>, now: number = Date.now()): { who: string; what: string; when: string } | null {
  const c = claimOf(item);
  if (!c || c.mine) return null;
  return { who: `${firstName(c.by)} has`, what: item.headline, when: `taken ${clock(c.at, now)}` };
}

/** A decide answer's claim facts, read off the route's JSON (the portal's DecideResult type predates them). */
export function claimExtra(r: unknown): ClaimDecideExtra {
  const o = obj(r);
  const claim = obj(o?.claim);
  const asked = obj(o?.asked);
  return {
    claim: claim && typeof claim.id === 'string' ? { id: claim.id } : null,
    asked: asked && typeof asked.requestId === 'string'
      ? { requestId: asked.requestId, to: String(asked.to ?? ''), name: String(asked.name ?? ''), landed: asked.landed === true, refused: typeof asked.refused === 'string' ? asked.refused : null }
      : null,
  };
}

/** What the card says after the press. */
export function saidAfter(kind: string, r: unknown, label: string): string {
  const x = claimExtra(r);
  if (kind === 'take') return 'It is yours. The task is under Tasks with its due date; the others see you have it.';
  if (kind === 'give_back') return 'Given back. It is untaken again for everyone it is with; your task is parked under its history.';
  if (kind === 'ask_to_take' && x.asked) return x.asked.landed ? `Asked ${firstName(x.asked.name)}. It is in their ten; they say yes or no.` : `Asked ${firstName(x.asked.name)}. ${x.asked.refused ?? ''}`.trim();
  return `Chosen: ${label}.`;
}

/** The awareness strip leads with who has what (U25: awareness is not reduced by a take). */
export function claimLinesFirst<T extends Pick<PassItem, 'features'>>(strip: T[]): T[] {
  const lines = strip.filter((i) => claimOf(i) && !claimOf(i)!.mine);
  return [...lines, ...strip.filter((i) => !lines.includes(i))];
}
